import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UserAvatar from "./UserAvatar";
import { useCurrency } from "../services/currency";

const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const formatPrice = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

export function FeaturedCourseSkeleton() {
    return (
        <div className="fc-card fc-card--skeleton" aria-hidden="true">
            <div className="fc-cover" />
            <div className="fc-body">
                <span className="fc-sk-line" style={{ width: "80%" }} />
                <span className="fc-sk-line" style={{ width: "95%" }} />
                <span className="fc-sk-line" style={{ width: "60%" }} />
            </div>
        </div>
    );
}

// Course card used on the home page (Featured Courses) and /all-courses. Other listings keep CourseCard.
export default function FeaturedCourseCard({ course }) {
    const { t } = useTranslation();
    const { formatPrice: money } = useCurrency();
    const location = useLocation();
    const isAppRoute = location.pathname.startsWith("/app");
    const [coverFailed, setCoverFailed] = useState(false);

    const href = isAppRoute ? `/app/course/${course.slug}` : `/course/${course.slug}`;
    const isRecorded = course.course_type === "recorded";
    const rawPrice = isRecorded ? course.course_price : course?.trial_lessons?.lesson_price;
    const price = formatPrice(rawPrice);
    const rating = Number(course.avg_rating);
    const hasCover = course.cover_image && !coverFailed;

    return (
        <Link to={href} className="fc-card">
            <div className="fc-cover">
                {hasCover ? (
                    <img src={course.cover_image} alt="" loading="lazy" onError={() => setCoverFailed(true)} />
                ) : (
                    <div className="fc-cover-fallback" aria-hidden="true">
                        <i className="fa-light fa-book-open" />
                    </div>
                )}
                {course.category && <span className="fc-chip">{course.category}</span>}
                {isRecorded ? (
                    <span className="fc-type">
                        <i className="fa-solid fa-circle-play" /> {t("homeCourse.recorded")}
                    </span>
                ) : (
                    <span className="fc-type fc-type--live">
                        <span className="fc-live-dot" /> {t("homeCourse.live")}
                    </span>
                )}
            </div>

            <div className="fc-body">
                <h3 className="fc-title">{course.title}</h3>
                <p className="fc-desc">{stripHtml(course.description)}</p>

                <div className="fc-teacher">
                    <UserAvatar src={course?.teacher?.avatar} name={course?.teacher?.name} size={32} className="fc-avatar" />
                    <span className="fc-teacher-name">{course?.teacher?.name}</span>
                </div>

                <div className="fc-meta">
                    <span className="fc-meta-item">
                        <i className="fa-light fa-book-open" />
                        {t("courseCard.lessons_count", { count: course.lessons_count ?? 0 })}
                    </span>
                    {rating > 0 ? (
                        <span className="fc-meta-item fc-rating">
                            <i className="fa-solid fa-star" /> {rating.toFixed(1)}
                        </span>
                    ) : (
                        <span className="fc-meta-item fc-new">{t("homeCourse.new")}</span>
                    )}
                    <span className="fc-price">
                        {price === null ? null : Number(price) === 0 ? (
                            t("homeCourse.free")
                        ) : (
                            <>
                                <strong>{money(rawPrice)}</strong>
                                {!isRecorded && <small>{t("homeCourse.per_lesson")}</small>}
                            </>
                        )}
                    </span>
                </div>
            </div>
        </Link>
    );
}
